import React, { FC } from 'react'
import {
  Button,
  ModalActions,
  ModalContent,
  ModalHeader,
} from '@lightspeed/design-system-react'
import { useT, UT } from '@transifex/react'
import VendNumber from 'vend-number'
import { useVdCurrencyFilter } from '../../../../../../../ng-compat/hooks'

interface RefundRestrictionPromptProps {
  close: () => void
  retailerPaymentTypeName: string
  refundableAmount: VendNumber
}

export const RefundRestrictionPrompt: FC<RefundRestrictionPromptProps> = ({
  close,
  retailerPaymentTypeName,
  refundableAmount,
}) => {
  const t = useT()
  const vdCurrencyFilter = useVdCurrencyFilter()
  const isZeroBalance = refundableAmount.isZero()
  return (
    <>
      <ModalHeader>
        {t(`This refund with ${retailerPaymentTypeName} can't be processed.`, {
          _context:
            'The lightspeed payments refund prompt header informs the user that the refund request cannot be processed',
          retailerPaymentTypeName,
        })}
      </ModalHeader>
      <ModalContent>
        <p
          className="vd-p"
          data-testid="server-driven-refund-restriction-message"
        >
          {isZeroBalance ? (
            t(
              `The total original amount paid by {retailerPaymentTypeName} on this sale has been refunded. Choose another payment type to refund the balance.`,
              {
                retailerPaymentTypeName,
                _context:
                  'The Lightspeed Payments refund prompt message informs the user that the original amount paid has been refunded and that the balance can be refunded using another payment type.',
              }
            )
          ) : (
            <UT
              _inline
              _str={`The original amount paid by {retailerPaymentTypeName} on this sale was <strong>{refundableAmount}</strong>. You can refund up to this amount with {retailerPaymentTypeName}, then refund the balance with another payment type.`}
              retailerPaymentTypeName={retailerPaymentTypeName}
              refundableAmount={vdCurrencyFilter(refundableAmount.toNumber())}
              _context={`The Lightspeed Payments refund prompt message informs the user that the original amount paid and explains that the refund can be made using the same payment type up to that amount, with the balance refunded through another payment type.`}
            />
          )}
        </p>
      </ModalContent>
      <ModalActions>
        <div className="vd-btn-group">
          <Button
            data-track={`${
              isZeroBalance ? 'got-it' : 'choose-another-payment'
            }-refund-restriction-modal`}
            variant="supplementary"
            onClick={close}
          >
            {isZeroBalance
              ? t('Got it, thanks!', {
                  _context: 'Button label',
                })
              : t('Choose a different payment method', {
                  _context: 'Button label',
                })}
          </Button>
        </div>
      </ModalActions>
    </>
  )
}
